import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Dataset, ReferenceControl } from '../../../types/types';
import { updateActiveFiltersFromLazyState } from '../../../utils/filters/updateActiveFiltersFromLazyState';
import { getLockedFilterParams } from '../utils/getLockedFilterParams';
import { getReferenceFilterParamObject } from '../utils/getReferenceFilterParamObject';

type ActiveFilters = Record<string, any> | null;

type UseReferenceFiltersResult = {
  // USER layer — what the user typed into the grid's quickfilter row.
  // null = not initialised yet (the first-fetch gate waits for non-null).
  activeFilters: ActiveFilters;
  // Same value for fetch closures — state can be stale inside async callbacks.
  activeFiltersRef: MutableRefObject<ActiveFilters>;
  // LOCKED layer — see the model comment below.
  defaultFilter: Record<string, any> | undefined;
  // filterParams the locked layer constrains (or/and groups included) — their
  // columns must not offer a quickfilter, the user would always lose to the lock.
  lockedFilterParams: string[];
  // Both layers merged raw (unconverted), state-based — for render-time
  // consumers: item `visible` expression resolution and the requestData context.
  rawMergedFilters: ActiveFilters;
  getRawMergedFilters: () => ActiveFilters;
  getEffectiveFilterObject: () => Record<string, any>;
  resetActiveFilters: () => void;
  applyLazyStateFilters: (lazyState: any, dataset: Dataset) => void;
};

// ── Filtering model of FullscreenReference: two independent layers ──────────
//
// 1. defaultFilter — the LOCKED layer. Comes from the form definition
//    (control.filter, already resolved to a plain object before the modal
//    opens). It says "this reference may only ever show this subset of the
//    dataset" (e.g. only suppliers of the selected store). The user must not
//    be able to see, edit or remove it, so it is NEVER written into
//    activeFilters/lazyState (the quickfilter UI state). Instead it is merged
//    into every single request the reference sends.
//
// 2. activeFilters — the USER layer. Only what the user types into the grid's
//    quickfilter row. Narrows the locked subset further, never widens it.
//
// Every fetch sends: locked layer + user layer (locked wins on the same key).
// If you add a new fetch/probe to FullscreenReference, it MUST include
// defaultFilter too, otherwise it escapes the locked subset.
export const useReferenceFilters = (
  control: ReferenceControl
): UseReferenceFiltersResult => {
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(null);

  const activeFiltersRef = useRef<ActiveFilters>(activeFilters);

  const defaultFilter = useMemo(
    () =>
      control.filter && typeof control.filter === 'object'
        ? control.filter
        : undefined,
    [control.filter]
  );

  const lockedFilterParams = useMemo(
    () => getLockedFilterParams(defaultFilter),
    [defaultFilter]
  );

  const rawMergedFilters = useMemo(
    () =>
      defaultFilter
        ? { ...(activeFilters ?? {}), ...defaultFilter }
        : activeFilters,
    [activeFilters, defaultFilter]
  );

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  const getRawMergedFilters = (): ActiveFilters =>
    defaultFilter
      ? { ...(activeFiltersRef.current ?? {}), ...defaultFilter }
      : activeFiltersRef.current;

  // User layer converted to request shape, locked layer spread on top.
  // defaultFilter is already request-shaped (it may contain nested or/and
  // groups), so it must NOT go through getReferenceFilterParamObject again.
  const getEffectiveFilterObject = (): Record<string, any> => ({
    ...getReferenceFilterParamObject(activeFiltersRef.current ?? {}),
    ...(defaultFilter ?? {}),
  });

  const resetActiveFilters = () => setActiveFilters({});

  const applyLazyStateFilters = (lazyState: any, dataset: Dataset) =>
    setActiveFilters((oldActiveFilters) =>
      updateActiveFiltersFromLazyState(oldActiveFilters, lazyState, dataset)
    );

  return {
    activeFilters,
    activeFiltersRef,
    defaultFilter,
    lockedFilterParams,
    rawMergedFilters,
    getRawMergedFilters,
    getEffectiveFilterObject,
    resetActiveFilters,
    applyLazyStateFilters,
  };
};
